import { useEffect, useMemo, useState } from 'react';
import { FiEdit3, FiImage, FiSave, FiTrash2, FiX } from 'react-icons/fi';
import { TableHeader, TablePerPage } from '../../../../../_CORE/APP/Templates/_UI/Table/_index';
import { FooterPagination } from '../../../../../_CORE/APP/Templates/Layouts/Section/Table/_Plugins/FooterPagination';
import { usePortfolioAdmin } from '../_Hooks/usePortfolioAdmin';
import { RESOLVE_PORTFOLIO_MEDIA_URL } from '../../../_Shared/resolveMediaUrl';
import { confirm } from '../../../../../_CORE/APP/Templates/_UI/Confirm/AppConfirmHost';

const DEFAULT_PER_PAGE = 12;
const MAX_IMAGE_BYTES = 8 * 1024 * 1024;

const revokePreview = (value) => {
    if (value && String(value).startsWith('blob:')) URL.revokeObjectURL(value);
};

const emptyValues = (fields) => Object.fromEntries(fields.map((field) => [
    field.name,
    field.type === 'checkbox' ? !!field.defaultValue : (field.defaultValue ?? ''),
]));

const mapValues = (item, fields) => Object.fromEntries(fields.map((field) => {
    const value = item?.[field.name];
    if (field.type === 'tags') return [field.name, Array.isArray(value) ? value.join(', ') : (value || '')];
    if (field.type === 'checkbox') return [field.name, value === true || Number(value) === 1];
    return [field.name, value ?? ''];
}));

const onNormalizeText = (value = '') => String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

const onSplitTokens = (value) => {
    if (Array.isArray(value)) return value.flatMap(onSplitTokens);
    if (value === null || value === undefined || value === '') return [];

    return String(value)
        .split(/[,;|·]+/g)
        .map((item) => item.trim())
        .filter(Boolean);
};

const onOptionValue = (value) => {
    if (value && typeof value === 'object' && !Array.isArray(value) && 'value' in value) return value.value;
    return value;
};

const onDefaultFilterValue = (type) => {
    if (type === 'multi') return [];
    if (type === 'dateRange') return [null, null];
    return null;
};

const onFieldIsVisible = (field, values) => {
    if (typeof field?.visibleWhen === 'function') return !!field.visibleWhen(values);
    return true;
};

/*
    Todas las colecciones del admin comparten filtros temporales.
    El backend MySQL entrega timestamps de registro; si una coleccion tiene
    una fecha propia (por ejemplo Blog/published_at), la vista puede declarar
    sus filtros y estos defaults no se duplican.
*/
const onResolveRecordDate = (item) => (
    item?.created_at
    ?? item?.updated_at
    ?? item?.date
    ?? item?.published_at
    ?? null
);

const ADMIN_DEFAULT_DATE_FILTERS = [
    {
        key: 'record_date_exact',
        label: 'Fecha exacta',
        type: 'date',
        placeholder: 'Elegir fecha',
        autoRecordDate: true,
        getValues: (item) => [onResolveRecordDate(item)],
    },
    {
        key: 'record_date_range',
        label: 'Rango de fechas',
        type: 'dateRange',
        placeholder: 'Desde — hasta',
        col: 2,
        autoRecordDate: true,
        getValues: (item) => [onResolveRecordDate(item)],
    },
];

const onFilterIsActive = (type, value) => {
    if (type === 'dateRange') return Array.isArray(value) && value.some(Boolean);
    if (type === 'multi') return Array.isArray(value) && value.length > 0;
    return value !== null && value !== undefined && value !== '';
};

const onFieldValues = (item, filter) => {
    if (typeof filter.getValues === 'function') {
        const resolved = filter.getValues(item);
        return Array.isArray(resolved) ? resolved : [resolved];
    }

    const raw = item?.[filter.field ?? filter.key];

    if (filter.tokenize) return onSplitTokens(raw);
    return Array.isArray(raw) ? raw : [raw];
};

const onBuildDynamicOptions = (items, filter) => {
    if (Array.isArray(filter.options)) return filter.options;
    if (!['select', 'multi'].includes(filter.type)) return [];

    const values = items
        .flatMap((item) => onFieldValues(item, filter))
        .filter((value) => value !== null && value !== undefined && String(value).trim() !== '');

    const UNIQUE = new Map();

    values.forEach((value) => {
        const label = String(value).trim();
        const key = onNormalizeText(label);
        if (!UNIQUE.has(key)) UNIQUE.set(key, { value: label, label });
    });

    return [...UNIQUE.values()].sort((a, b) => a.label.localeCompare(b.label, 'es', { sensitivity: 'base' }));
};

const onDateValue = (value) => {
    if (!value) return null;
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : new Date(value.getTime());

    const TEXT = String(value).trim();
    const DATE_ONLY = TEXT.match(/^(\d{4})-(\d{2})-(\d{2})$/);

    if (DATE_ONLY) {
        const [, year, month, day] = DATE_ONLY;
        return new Date(Number(year), Number(month) - 1, Number(day));
    }

    const DATE = new Date(TEXT);
    return Number.isNaN(DATE.getTime()) ? null : DATE;
};

const onDateKey = (value) => {
    const DATE = onDateValue(value);
    if (!DATE) return '';

    const year = DATE.getFullYear();
    const month = String(DATE.getMonth() + 1).padStart(2, '0');
    const day = String(DATE.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const onMatchesFilter = (item, filter, value) => {
    if (!onFilterIsActive(filter.type, value)) return true;
    if (typeof filter.matches === 'function') return filter.matches(item, value);

    const ITEM_VALUES = onFieldValues(item, filter);

    if (filter.type === 'date') {
        const expected = onDateKey(value);
        return ITEM_VALUES.some((itemValue) => onDateKey(itemValue) === expected);
    }

    if (filter.type === 'dateRange') {
        const [from, to] = value ?? [null, null];
        const FROM = onDateValue(from);
        const TO = onDateValue(to);

        if (FROM) FROM.setHours(0, 0, 0, 0);
        if (TO) TO.setHours(23, 59, 59, 999);

        return ITEM_VALUES.some((itemValue) => {
            const CURRENT = onDateValue(itemValue);
            if (!CURRENT) return false;
            if (FROM && CURRENT < FROM) return false;
            if (TO && CURRENT > TO) return false;
            return true;
        });
    }

    if (filter.type === 'multi') {
        const SELECTED = (value ?? []).map(onOptionValue).map(onNormalizeText).filter(Boolean);
        const CURRENT = ITEM_VALUES.map(onNormalizeText).filter(Boolean);

        if (filter.matchMode === 'all') return SELECTED.every((selected) => CURRENT.includes(selected));
        return SELECTED.some((selected) => CURRENT.includes(selected));
    }

    if (filter.type === 'select') {
        const SELECTED = onNormalizeText(onOptionValue(value));
        return ITEM_VALUES.some((itemValue) => onNormalizeText(itemValue) === SELECTED);
    }

    const EXPECTED = onNormalizeText(onOptionValue(value));
    return ITEM_VALUES.some((itemValue) => onNormalizeText(itemValue).includes(EXPECTED));
};

const onCompareBy = (a, b, option, titleField) => {
    if (typeof option?.compare === 'function') return option.compare(a, b);

    const FIELD = option?.field ?? (String(option?.value).startsWith('title-') ? titleField : 'sort_order');
    const DIRECTION = option?.direction ?? (String(option?.value).endsWith('-desc') ? 'desc' : 'asc');
    const TYPE = option?.type ?? (FIELD === 'sort_order' ? 'number' : 'text');

    let RESULT = 0;

    if (TYPE === 'number') {
        RESULT = Number(a?.[FIELD] ?? 0) - Number(b?.[FIELD] ?? 0);
    } else if (TYPE === 'date') {
        const A = onDateValue(a?.[FIELD])?.getTime() ?? 0;
        const B = onDateValue(b?.[FIELD])?.getTime() ?? 0;
        RESULT = A - B;
    } else {
        RESULT = String(a?.[FIELD] ?? '').localeCompare(String(b?.[FIELD] ?? ''), 'es', { sensitivity: 'base' });
    }

    return DIRECTION === 'desc' ? -RESULT : RESULT;
};

export const AdminCollectionEditor = ({
    title,
    description,
    listEndpoint,
    saveEndpoint,
    removeEndpoint,
    fields = [],
    titleField = 'title',
    subtitleField = null,
    imageField = null,
    searchFields = null,
    filters = [],
    orderOptions = [],
}) => {
    const api = usePortfolioAdmin();
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const [values, setValues] = useState(() => emptyValues(fields));
    const [files, setFiles] = useState({});
    const [filePreviews, setFilePreviews] = useState({});
    const [imageErrors, setImageErrors] = useState({});
    const [fileInputVersion, setFileInputVersion] = useState(0);
    const [message, setMessage] = useState('');
    const [formOpen, setFormOpen] = useState(false);

    const [search, setSearch] = useState('');
    const [filterValues, setFilterValues] = useState({});
    const [order, setOrder] = useState(() => orderOptions[0]?.value ?? 'sort-asc');
    const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE);
    const [page, setPage] = useState(1);

    const load = async () => {
        try {
            const data = await api.get(listEndpoint);
            setItems(Array.isArray(data) ? data : []);
            setMessage('');
        } catch (err) { setMessage(err.message); }
    };

    useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [listEndpoint]);

    useEffect(() => () => {
        Object.values(filePreviews).forEach(revokePreview);
    }, [filePreviews]);

    const resetPendingImages = () => {
        Object.values(filePreviews).forEach(revokePreview);
        setFilePreviews({});
        setFiles({});
        setImageErrors({});
        setFileInputVersion((value) => value + 1);
    };

    const selectImage = (fieldName, file) => {
        setMessage('');
        setImageErrors((prev) => ({ ...prev, [fieldName]: false }));

        if (!file) {
            setFiles((prev) => ({ ...prev, [fieldName]: null }));
            setFilePreviews((prev) => {
                revokePreview(prev[fieldName]);
                const next = { ...prev };
                delete next[fieldName];
                return next;
            });
            return;
        }

        if (!String(file.type || '').startsWith('image/')) {
            setMessage('El archivo seleccionado no es una imagen valida.');
            return;
        }
        if (file.size > MAX_IMAGE_BYTES) {
            setMessage('La imagen supera el limite de 8 MB.');
            return;
        }

        const preview = URL.createObjectURL(file);
        setFiles((prev) => ({ ...prev, [fieldName]: file }));
        setFilePreviews((prev) => {
            revokePreview(prev[fieldName]);
            return { ...prev, [fieldName]: preview };
        });
    };

    const startCreate = () => {
        setEditing(null);
        setValues(emptyValues(fields));
        resetPendingImages();
        setFormOpen(true);
        setMessage('');
    };

    const startEdit = (item) => {
        setEditing(item);
        setValues(mapValues(item, fields));
        resetPendingImages();
        setFormOpen(true);
        setMessage('');
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const close = () => {
        setFormOpen(false);
        setEditing(null);
        resetPendingImages();
    };

    const submit = async (event) => {
        event.preventDefault();
        const body = new FormData();
        if (editing?.id) body.append('id', editing.id);
        fields.forEach((field) => {
            if (field.type === 'image') {
                if (files[field.name]) body.append(field.name, files[field.name], files[field.name].name);
                return;
            }
            body.append(field.name, field.type === 'checkbox' ? (values[field.name] ? '1' : '0') : (values[field.name] ?? ''));
        });
        try {
            await api.securePost(saveEndpoint, body);
            close();
            await load();
        } catch (err) { setMessage(err.message); }
    };

    const remove = async (item) => {
        if (!(await confirm.danger(`Eliminar "${item[titleField] || 'registro'}"?`, 'Esta accion no se puede deshacer.'))) return;
        try {
            await api.securePost(removeEndpoint, { id: item.id });
            await load();
        } catch (err) { setMessage(err.message); }
    };

    const HAS_RECORD_DATES = useMemo(() => items.some((item) => !!onResolveRecordDate(item)), [items]);

    /*
        Los filtros temporales automaticos solo aparecen cuando la API entrega
        una fecha real para esa coleccion. Si no hay created_at/updated_at/date,
        no mostramos controles deshabilitados que no pueden filtrar nada.

        Las fechas declaradas explicitamente por una vista (Blog/published_at)
        se conservan siempre porque forman parte de esa entidad.
    */
    const EFFECTIVE_FILTERS = useMemo(() => {
        const hasExactDate = filters.some((filter) => filter.type === 'date');
        const hasDateRange = filters.some((filter) => filter.type === 'dateRange');

        return [
            ...filters,
            ...(HAS_RECORD_DATES && !hasExactDate ? [ADMIN_DEFAULT_DATE_FILTERS[0]] : []),
            ...(HAS_RECORD_DATES && !hasDateRange ? [ADMIN_DEFAULT_DATE_FILTERS[1]] : []),
        ];
    }, [filters, HAS_RECORD_DATES]);

    const RESOLVED_FILTERS = useMemo(() => EFFECTIVE_FILTERS.map((filter) => ({
        ...filter,
        options: onBuildDynamicOptions(items, filter),
    })), [EFFECTIVE_FILTERS, items]);

    const RESOLVED_ORDER_OPTIONS = useMemo(() => {
        if (orderOptions.length) return orderOptions;

        return [
            { value: 'sort-asc', label: 'Orden manual ↑', field: 'sort_order', direction: 'asc', type: 'number' },
            { value: 'sort-desc', label: 'Orden manual ↓', field: 'sort_order', direction: 'desc', type: 'number' },
            { value: 'title-asc', label: 'Nombre A → Z', field: titleField, direction: 'asc', type: 'text' },
            { value: 'title-desc', label: 'Nombre Z → A', field: titleField, direction: 'desc', type: 'text' },
        ];
    }, [orderOptions, titleField]);

    useEffect(() => {
        if (!RESOLVED_ORDER_OPTIONS.some((option) => option.value === order)) {
            setOrder(RESOLVED_ORDER_OPTIONS[0]?.value ?? 'sort-asc');
        }
    }, [RESOLVED_ORDER_OPTIONS, order]);

    const HEADER_FILTERS = useMemo(() => RESOLVED_FILTERS.map((filter) => ({
        ...filter,
        value: filterValues[filter.key] ?? onDefaultFilterValue(filter.type),
        onChange: (value) => setFilterValues((prev) => ({ ...prev, [filter.key]: value })),
    })), [RESOLVED_FILTERS, filterValues]);

    const ACTIVE_SEARCH_FIELDS = useMemo(() => {
        if (Array.isArray(searchFields) && searchFields.length) return searchFields;
        return [titleField, subtitleField].filter(Boolean);
    }, [searchFields, titleField, subtitleField]);

    const FILTERED = useMemo(() => {
        const TERM = onNormalizeText(search);

        const rows = items.filter((item) => {
            if (TERM) {
                const MATCHES_SEARCH = ACTIVE_SEARCH_FIELDS.some((field) => {
                    const raw = typeof field === 'function' ? field(item) : item?.[field];
                    const content = Array.isArray(raw) ? raw.join(' ') : raw;
                    return onNormalizeText(content).includes(TERM);
                });

                if (!MATCHES_SEARCH) return false;
            }

            return RESOLVED_FILTERS.every((filter) => (
                onMatchesFilter(item, filter, filterValues[filter.key] ?? onDefaultFilterValue(filter.type))
            ));
        });

        const ACTIVE_ORDER = RESOLVED_ORDER_OPTIONS.find((option) => option.value === order) ?? RESOLVED_ORDER_OPTIONS[0];
        return [...rows].sort((a, b) => onCompareBy(a, b, ACTIVE_ORDER, titleField));
    }, [items, search, ACTIVE_SEARCH_FIELDS, RESOLVED_FILTERS, filterValues, RESOLVED_ORDER_OPTIONS, order, titleField]);

    useEffect(() => { setPage(1); }, [search, filterValues, order, perPage]);

    const TOTAL_PAGES = perPage === -1 ? 1 : Math.max(1, Math.ceil(FILTERED.length / perPage));
    const SAFE_PAGE = Math.min(page, TOTAL_PAGES);
    const PAGE_ITEMS = perPage === -1
        ? FILTERED
        : FILTERED.slice((SAFE_PAGE - 1) * perPage, SAFE_PAGE * perPage);

    useEffect(() => {
        if (page !== SAFE_PAGE) setPage(SAFE_PAGE);
    }, [page, SAFE_PAGE]);

    const clearFilters = () => {
        setSearch('');
        setFilterValues({});
        setPage(1);
    };

    const FROM = FILTERED.length === 0 ? 0 : (perPage === -1 ? 1 : ((SAFE_PAGE - 1) * perPage) + 1);
    const TO = FILTERED.length === 0 ? 0 : (perPage === -1 ? FILTERED.length : Math.min(SAFE_PAGE * perPage, FILTERED.length));

    return (
        <section className="PBADMIN__panel PBADMIN__collection_panel" data-editing={formOpen ? "true" : "false"}>
            <div className="PBADMIN__collection_tools">
                <TableHeader
                    pill="GESTIÓN"
                    title="Administrar"
                    titleAccent={title}
                    subtitle={description}
                    newLabel="Nuevo"
                    onNew={startCreate}
                    search={search}
                    onSearch={setSearch}
                    searchPlaceholder={`Buscar en ${title.toLowerCase()}…`}
                    orderOptions={RESOLVED_ORDER_OPTIONS}
                    order={order}
                    onOrder={setOrder}
                    filters={HEADER_FILTERS}
                    onClearFilters={clearFilters}
                    isDisabled={formOpen}
                    stats={(
                        <div className="PBADMIN__filter_summary" aria-live="polite">
                            <span><strong>{FILTERED.length}</strong> resultado{FILTERED.length === 1 ? '' : 's'}</span>
                            <span>{items.length} total</span>
                        </div>
                    )}
                />
            </div>

            {message && <div className="PBADMIN__alert">{message}</div>}

            {formOpen && (
                <form className="PBADMIN__editor PBADMIN__form_grid" onSubmit={submit}>
                    <div className="PBADMIN__editor_head"><strong>{editing ? 'Editar registro' : 'Nuevo registro'}</strong><button type="button" onClick={close}><FiX /></button></div>
                    {fields.map((field) => {
                        if (!onFieldIsVisible(field, values)) return null;
                        if (field.type === 'image') {
                            const current = editing?.[field.name];
                            const selected = files[field.name];
                            const source = filePreviews[field.name] || RESOLVE_PORTFOLIO_MEDIA_URL(current);
                            const canRender = !!source && !imageErrors[field.name];
                            return (
                                <label key={field.name} className={`PBADMIN__field PBADMIN__field--${field.span || 2}`}>
                                    <span>{field.label}</span>
                                    <div className="PBADMIN__image_field">
                                        <div className="PBADMIN__image_preview">
                                            {canRender ? <img src={source} alt={selected ? 'Vista previa' : 'Actual'} onLoad={() => setImageErrors((prev) => ({ ...prev, [field.name]: false }))} onError={() => setImageErrors((prev) => ({ ...prev, [field.name]: true }))} /> : <FiImage />}
                                        </div>
                                        <div className="PBADMIN__image_picker">
                                            <input key={`${field.name}-${fileInputVersion}`} type="file" accept="image/jpeg,image/png,image/webp,image/gif,image/avif" onChange={(e) => selectImage(field.name, e.target.files?.[0] || null)} />
                                            {selected && <small>Seleccionada: {selected.name} · {(selected.size / 1024 / 1024).toFixed(2)} MB</small>}
                                            {!selected && current && imageErrors[field.name] && <small className="PBADMIN__image_warning">La imagen guardada no existe o no es accesible. Selecciona una nueva para reemplazarla.</small>}
                                        </div>
                                    </div>
                                </label>
                            );
                        }
                        if (field.type === 'textarea') return <label key={field.name} className={`PBADMIN__field PBADMIN__field--${field.span || 2}`}><span>{field.label}</span><textarea rows={field.rows || 4} required={!!field.required} value={values[field.name]} onChange={(e) => setValues((prev) => ({ ...prev, [field.name]: e.target.value }))} /></label>;
                        if (field.type === 'checkbox') return (
                            <label key={field.name} className="PBADMIN__check">
                                <input
                                    type="checkbox"
                                    checked={!!values[field.name]}
                                    onChange={(e) => {
                                        const checked = e.target.checked;
                                        setValues((prev) => {
                                            const next = { ...prev, [field.name]: checked };
                                            if (!checked && Array.isArray(field.clearFieldsWhenFalse)) {
                                                field.clearFieldsWhenFalse.forEach((name) => { next[name] = ''; });
                                            }
                                            return next;
                                        });
                                    }}
                                />
                                <span>{field.label}</span>
                            </label>
                        );
                        return <label key={field.name} className={`PBADMIN__field PBADMIN__field--${field.span || 1}`}><span>{field.label}</span><input type={field.type || 'text'} required={!!field.required} value={values[field.name]} onChange={(e) => setValues((prev) => ({ ...prev, [field.name]: e.target.value }))} /></label>;
                    })}
                    <div className="PBADMIN__form_footer"><button className="PBADMIN__primary" type="submit" disabled={api.isLoading}><FiSave /> {api.isLoading ? 'Guardando...' : 'Guardar'}</button><button type="button" className="PBADMIN__secondary" onClick={close}>Cancelar</button></div>
                </form>
            )}

            <div className="PBADMIN__collection" aria-disabled={formOpen ? "true" : "false"}>
                {FILTERED.length === 0 && !api.isLoading && (
                    <div className="PBADMIN__empty">
                        {items.length === 0 ? 'Todavia no hay registros.' : 'No hay resultados con los filtros actuales.'}
                    </div>
                )}

                {PAGE_ITEMS.map((item) => (
                    <article key={item.id} className="PBADMIN__row">
                        {imageField && <div className="PBADMIN__row_image">{item[imageField] ? <img src={RESOLVE_PORTFOLIO_MEDIA_URL(item[imageField])} alt="" /> : <FiImage />}</div>}
                        <div className="PBADMIN__row_copy"><strong>{item[titleField] || 'Sin titulo'}</strong>{subtitleField && item[subtitleField] && <span>{Array.isArray(item[subtitleField]) ? item[subtitleField].join(', ') : item[subtitleField]}</span>}<small>{item.active === false || Number(item.active) === 0 ? 'Oculto' : 'Visible'} · orden {item.sort_order ?? 0}</small></div>
                        <div className="PBADMIN__row_actions"><button type="button" onClick={() => startEdit(item)} title="Editar" disabled={formOpen}><FiEdit3 /></button><button type="button" onClick={() => remove(item)} title="Eliminar" className="is-danger" disabled={formOpen}><FiTrash2 /></button></div>
                    </article>
                ))}
            </div>

            {FILTERED.length > 0 && (
                <div className="PBADMIN__collection_footer">
                    <div className="PBADMIN__collection_count">
                        Mostrando <strong>{FROM}–{TO}</strong> de <strong>{FILTERED.length}</strong>
                    </div>

                    <TablePerPage
                        value={perPage}
                        onChange={setPerPage}
                        totalItems={FILTERED.length}
                        disabled={formOpen}
                    />

                    <FooterPagination
                        pagina={SAFE_PAGE}
                        totalPaginas={TOTAL_PAGES}
                        onIr={setPage}
                        disabled={formOpen}
                    />
                </div>
            )}
        </section>
    );
};
